<template>
  <div class="sm:px-5 px-0 max-w-7xl mx-auto">
    <GalleryHint />
    <GalleryTopNav />
    <div id="grid-top" class="bg-transparent h-0.5"></div>
    <main>
      <GallerySearch
        v-model="debouncedSearch"
        :art-type="artType"
        :loading="pending"
        @clear-search="clearSearch"
        v-if="!store.showSearch"
      />

      <!-- Desktop -->

      <div class="sm:block hidden w-full mt-2.5">
        <div class="w-full">
          <FiltersArtType
            :art-type="artType"
            :is-for-sale="isForSale"
            @filter-by-art-type="selectArtType"
          >
            <template #sales-filter>
              <FiltersForSale
                :is-for-sale="isForSale"
                @filter-by-sale="filterByIsForSale"
              />
            </template>
          </FiltersArtType>
        </div>
      </div>

      <!-- Mobile  -->

      <BaseModal
        :model-value="store.showSearch"
        @close="store.closeSearch"
        :title="$t('search.artwork_search')"
      >
        <div class="mb-5">
          <input
            type="text"
            class="w-full p-2.5 placeholder:text-sm border border-gray-200 focus:outline-2 focus:outline-primary-blue bg-gray-50 rounded-lg px-2.5"
            :placeholder="$t('search.search_placeholder_mobile')"
            :disabled="pending"
            v-model="debouncedSearch"
          />
        </div>
        <FiltersArtType
          :art-type="artType"
          :is-for-sale="isForSale"
          @filter-by-art-type="selectArtType"
        >
          <template #sales-filter>
            <FiltersForSale
              :is-for-sale="isForSale"
              @filter-by-sale="filterByIsForSale"
            />
          </template>
        </FiltersArtType>
      </BaseModal>

      <div class="flex-1 overflow-y-auto sm:pb-5 sm:mt-2.5">
        <ArtworkGallery
          :not-found-title="$t('not_found.artworks.title')"
          :not-found-label="$t('not_found.artworks.label')"
          :action="true"
          :items="artworks"
          :show-user-avatar="true"
          :loading="pending"
          :total-pages="meta?.totalPages || 0"
          :current-page="page"
          @change-page="changePage"
          @next="nextPage"
          @prev="prevPage"
          @not-found-action="resetFilters"
        />
      </div>
      <div class="" v-if="error">
        {{ error }}
      </div>
    </main>
  </div>
</template>

<script setup lang="ts">
import type { ArtworkDataMeta } from "~/stores/artworks.gallery";
import type { ArtType, Artwork } from "~~/types/artwork";
import { useDebounceFn } from "@vueuse/core";

const store = useArtworkStore();
const favorite = useFavoriteStore();

const artType = ref<ArtType>("");
const search = ref<string>("");
const page = ref<number>(1);
const limit = ref<number>(50);
const debouncedSearch = ref<string>("");
const isForSale = ref<boolean>(false);

const { data, pending, error } = await useFetch<any>("/api/artwork/artworks", {
  query: {
    limit,
    page,
    artType,
    search,
    isForSale,
  },
  watch: [limit, page, artType, search, isForSale],
});

const meta = computed<ArtworkDataMeta | null>(() => data.value?.meta || null);
const artworks = computed<Artwork[]>(() => data.value?.data || []);

watch(
  debouncedSearch,
  useDebounceFn(() => {
    search.value = debouncedSearch.value;
    store.closeSearch();
  }, 1000),
);

const scrollTop = () => {
  const element = document.getElementById("grid-top");
  if (!element) return;
  setTimeout(() => {
    element.scrollIntoView({
      behavior: "smooth",
      block: "end",
      inline: "nearest",
    });
  }, 100);
};

const selectArtType = (t: ArtType) => {
  page.value = 1;
  if (artType.value === t) {
    artType.value = "";
  } else {
    artType.value = t;
  }
  store.closeSearch();
};

const filterByIsForSale = () => {
  page.value = 1;
  isForSale.value = !isForSale.value;
  store.closeSearch();
};

const clearSearch = () => {
  debouncedSearch.value = "";
  search.value = "";
};

const changePage = (p: number) => {
  page.value = p;
  scrollTop();
};

const nextPage = () => {
  const totalPages = meta.value?.totalPages || 0;
  if (page.value < totalPages - 1) {
    page.value++;
    scrollTop();
  }
};

const prevPage = () => {
  if (page.value > 1) {
    page.value--;
    scrollTop();
  }
};

const resetFilters = async () => {
  search.value = "";
  artType.value = "";
  page.value = 1;
  isForSale.value = false;
};

const { t } = useI18n();

useSeoMeta({
  title: () => t("gallery_seo_meta.title"),
  description: () => t("gallery_seo_meta.description"),
  ogTitle: () => t("gallery_seo_meta.ogTitle"),
  ogDescription: () => t("gallery_seo_meta.ogDescription"),
  ogImage: "https://medium.am/og/logo.png",
  ogType: "website",
  twitterCard: "summary_large_image",
});
</script>
